import type { GetServerSideProps, NextPage } from "next";
import React from "react";
import getAppSettings, { AppSettings } from "../../../lib/get-app-settings";

import notFound from "../../../lib/not-found";
import authenticateViaUrlParameters from "../../../lib/authenticate-via-url-parameters";
import CategoryStandardPage, {
  getServerSideProps as getStandardPageProps,
  type TCategoryStandardPageProps,
} from "./_categorySlug.standard";
import CategoryLightPage, {
  getServerSideProps as getLightPageProps,
  type TCategoryLightPageProps,
} from "./_categorySlug.light";
import type {
  THelpCenterLightLayout,
  THelpCenterStandardLayout,
  HelpCenterLayout,
} from "../../../lib/load-company-settings";

export type TCategoryPageProps =
  | TCategoryStandardPageProps
  | TCategoryLightPageProps;

const CategoryPage: NextPage<TCategoryPageProps> = ({
  appSettings,
  ...props
}) => {
  if (appSettings.layout.startsWith("light_")) {
    return (
      <CategoryLightPage
        {...(props as TCategoryLightPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterLightLayout }
        }
      />
    );
  } else {
    return (
      <CategoryStandardPage
        {...(props as TCategoryStandardPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterStandardLayout }
        }
      />
    );
  }
};

export default CategoryPage;

export const getServerSideProps: GetServerSideProps<
  TCategoryPageProps
> = async (context) => {
  let appSettings: Awaited<ReturnType<typeof getAppSettings>> | undefined;
  try {
    appSettings = await getAppSettings(context);
  } catch (error: any) {
    console.error(error);
  }

  if (!appSettings) {
    return notFound(appSettings, context);
  }

  const authResponse = await authenticateViaUrlParameters(appSettings, context);
  if (authResponse) return authResponse;

  const previewLayout = context.query.preview_layout?.toString();
  if (
    previewLayout?.match(
      /^(light_sidebar|standard_(condensed|horizontal|grid))$/
    )
  ) {
    appSettings.layout = previewLayout as HelpCenterLayout;
  }

  if (appSettings.layout.startsWith("light_")) {
    return getLightPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterLightLayout }
    );
  } else {
    return getStandardPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterStandardLayout }
    );
  }
};
